/**
 * CORE-1156 — код реферальной ссылки на стороне клиента.
 *
 * Ссылку выдаёт бэкенд в виде `{frontend}/r/{code}` (`ReferralController.LinkUrl`),
 * а засчитывает переход `POST /api/referral/visit`. Здесь ровно две вещи, которые
 * нужны до сетевого вызова: привести код к тому виду, в котором его сравнивает
 * сервер, и отсеять то, что кодом не является вовсе.
 *
 * Нормализация повторяет серверную (`ReferralCode.Normalize`: trim + upper), и это
 * не дублирование правила, а его продолжение — код набирают руками с чужого
 * экрана, и «tlqp2kb4» обязан сработать так же, как «TLQP2KB4».
 *
 * А вот проверку алфавита сервера (`ReferralCode.IsWellFormed`: 8 символов из
 * «23456789BCDFGHJKLMNPQRSTVWXZ») мы сознательно НЕ копируем. Строгая копия молча
 * перестала бы засчитывать переходы в тот день, когда сервер выпустит код другой
 * длины или алфавита, — и выглядело бы это как «рефералка сломалась», без единой
 * ошибки в логах. Здесь стоит только грубый фильтр от мусора в адресе; решает
 * сервер, и на любой код он отвечает одинаково.
 */

/** Максимум, после которого строка в адресе — уже не код, а чей-то мусор. */
const MAX_CODE_LENGTH = 32;

/** Привести код к серверному виду сравнения. */
export function normalizeReferralCode(raw: string | null | undefined): string {
  return (raw ?? '').trim().toUpperCase();
}

/**
 * Стоит ли вообще идти в сеть с этим кодом.
 *
 * Не «правильный ли он» — на этот вопрос отвечает сервер. Здесь отсекается то,
 * что кодом быть не может ни при каком его формате: пустой путь `/r/`, пробелы,
 * пути с продолжением (`/r/foo/bar`), запросы браузера за иконками.
 */
export function isPlausibleReferralCode(raw: string | null | undefined): boolean {
  const code = normalizeReferralCode(raw);
  if (!code || code.length > MAX_CODE_LENGTH) return false;
  return /^[A-Z0-9]+$/.test(code);
}

/**
 * CORE-956 — id contextual-ссылки из `?l=` её адреса (`/r/{code}?l={id}`).
 *
 * Код в пути один на владельца, поэтому пост, эфир или кампания, под которые
 * ссылка выдана, различимы только по этому id. Всё, что не целое положительное
 * число, отбрасываем: переход тогда засчитается на общую ссылку, как и раньше.
 */
export function parseReferralLinkId(raw: string | number | null | undefined): number | undefined {
  const text = String(raw ?? '').trim();
  if (!/^\d{1,9}$/.test(text)) return undefined;
  const id = Number(text);
  return id > 0 ? id : undefined;
}

/** Чем кончился заход на посадочную. Наружу нужен только факт «дальше можно». */
export type ReferralLandingOutcome = 'recorded' | 'skipped' | 'failed' | 'timeout' | 'duplicate';

/**
 * Коды, уже засчитанные в этой загрузке страницы.
 *
 * Не перестраховка. Навигатор целиком завёрнут в `React.Suspense` (App.tsx), и
 * пока грузится чанк соседнего экрана, поддерево успевает смонтироваться дважды —
 * на превью это дало ДВА клика и две претензии на один переход, с разницей в
 * секунду. Клик — это цифра в воронке реферера, и удваивать её нельзя.
 *
 * Тем же гвардом закрыт возврат «назад» внутри SPA: перезагрузка вкладки — новый
 * переход и новая загрузка модуля, а хождение по истории — нет.
 */
const recordedCodes = new Set<string>();

/** Сколько ждать ответа, прежде чем увести человека дальше. */
export const REFERRAL_VISIT_TIMEOUT_MS = 3000;

/**
 * Засчитать переход и сказать, что можно идти дальше.
 *
 * Ответа ждём, потому что именно он кладёт токен посетителя в сторадж, а
 * регистрация обязана найти его на месте. Но ждём НЕ бесконечно и не выходим по
 * ошибке раньше времени: висящая сеть — не повод оставить человека на пустом
 * экране, а неудача перехода — не повод не пустить его на сайт. Поэтому исход
 * возвращается, а решение «куда дальше» у всех исходов одно.
 */
export async function recordReferralLanding(
  code: string | null | undefined,
  record: (code: string) => Promise<unknown>,
  timeoutMs: number = REFERRAL_VISIT_TIMEOUT_MS,
): Promise<ReferralLandingOutcome> {
  const normalized = normalizeReferralCode(code);
  if (!isPlausibleReferralCode(normalized)) return 'skipped';
  // Помечаем ДО вызова, а не после: второй монтаж успевает начаться раньше,
  // чем вернётся ответ первого, — на превью между ними была секунда.
  if (recordedCodes.has(normalized)) return 'duplicate';
  recordedCodes.add(normalized);

  let timer: ReturnType<typeof setTimeout> | undefined;
  const guard = new Promise<ReferralLandingOutcome>((resolve) => {
    timer = setTimeout(() => resolve('timeout'), timeoutMs);
  });
  const call = record(normalized)
    .then((): ReferralLandingOutcome => 'recorded')
    .catch((): ReferralLandingOutcome => 'failed');

  try {
    return await Promise.race([call, guard]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}
